import { useState } from 'react'
import { MessageSquareText } from 'lucide-react'
import { Button, Skeleton } from '../../core/index.js'
import { useWorkerRoster } from '../../roster/index.js'
import { useWorkerBarriers, useWorkerContacts, useWorkerDeliveries, useWorkerInboundTexts } from '../hooks.js'
import { InboundTextList } from './InboundTextList.js'
import { StaffMessageComposer } from './StaffMessageComposer.js'
import { ContactsSummary } from './ContactsSummary.js'
import { DeliveryReceiptsList } from './DeliveryReceiptsList.js'
import { isNotFoundError } from '../../core/index.js'
import { EndpointHint } from '../../core/index.js'
import { BarrierList } from './BarrierList.js'
import { useBarrierActions } from '../../roster/index.js'
import { LoadFailure } from '../../core/index.js'

/**
 * The Messages tab of an open roster row: what this one worker has said
 * is getting in the way, newest first, with mark seen and reply for the
 * facility's staff. Only this worker's notes; the facility has no list of
 * everyone's. Under them, how the worker is reached beyond the app and
 * where their messages went (Clinical Bridge plan section 13.6), read-only:
 * the worker alone adds a channel (D15).
 */
export function WorkerMessagesTab({ workerId, facilityId = null }: { workerId: string; /** The facility the row belongs to; with it, staff can write to this worker (SMS plan 3.7). */ facilityId?: string | null }) {
  const barriersQuery = useWorkerBarriers(workerId)
  const actions = useBarrierActions()
  const contactsQuery = useWorkerContacts(workerId)
  const deliveriesQuery = useWorkerDeliveries(workerId, { limit: 20 })
  const inboundQuery = useWorkerInboundTexts(workerId)
  const roster = useWorkerRoster(facilityId ?? '')
  const name = roster.data?.find((row) => row.workerId === workerId)?.name ?? workerId
  const [composing, setComposing] = useState(false)
  return (
    <div className="bg-canvas px-5 pb-5 pt-4 flex flex-col gap-5">
      {facilityId && (
        <div>
          <div className="mb-1.5 flex flex-wrap items-baseline gap-x-2 text-[9px] font-bold uppercase tracking-wide text-ink-mid">
            Write to {name}
            <EndpointHint>POST /facilities/{'{facilityId}'}/messages</EndpointHint>
          </div>
          {composing ? (
            <div className="bg-white border border-marine/15 rounded p-3">
              <StaffMessageComposer facilityId={facilityId} worker={{ workerId, name }} onSent={() => void deliveriesQuery.refetch()} />
              <button type="button" className="mt-2 text-[11px] font-bold text-ink-mid hover:text-coral" onClick={() => setComposing(false)}>
                Close
              </button>
            </div>
          ) : (
            <Button icon={MessageSquareText} size="sm" variant="secondary" onClick={() => setComposing(true)}>
              Message
            </Button>
          )}
        </div>
      )}
      <div>
      <div className="mb-1.5 flex flex-wrap items-baseline gap-x-2 text-[9px] font-bold uppercase tracking-wide text-ink-mid">
        What’s getting in the way — in their words
        <EndpointHint>GET /workers/{'{workerId}'}/barriers</EndpointHint>
      </div>
      {barriersQuery.isPending ? (
        <Skeleton />
      ) : isNotFoundError(barriersQuery.error) ? (
        <div className="text-xs text-ink-mid">This API does not have barriers yet; deploy it to switch this on.</div>
      ) : barriersQuery.isError ? (
        <LoadFailure size="xs" message="Could not load what this worker has said." onRetry={() => void barriersQuery.refetch()} retrying={barriersQuery.isFetching} />
      ) : (
        <BarrierList barriers={barriersQuery.data} audience="staff" {...actions} empty="Nothing yet. A worker whose score is short on a behavior can write here from their own app; the facility's staff mark each seen or reply." />
      )}
      </div>
      <div>
        <div className="mb-1.5 flex flex-wrap items-baseline gap-x-2 text-[9px] font-bold uppercase tracking-wide text-ink-mid">
          Replies by text — in their words
          <EndpointHint>GET /workers/{'{workerId}'}/inbound-texts</EndpointHint>
        </div>
        {inboundQuery.isPending ? <Skeleton /> : inboundQuery.isError ? <div className="text-xs text-ink-mid">Not available on this API yet.</div> : <InboundTextList texts={inboundQuery.data.items} empty="Nothing yet. A worker who replies to a text with their own words is heard here; STOP and START act on their number instead." />}
      </div>
      <div>
        <div className="mb-1.5 flex flex-wrap items-baseline gap-x-2 text-[9px] font-bold uppercase tracking-wide text-ink-mid">
          How they are reached
          <EndpointHint>GET /workers/{'{workerId}'}/contacts</EndpointHint>
        </div>
        {contactsQuery.isPending ? <Skeleton /> : contactsQuery.isError ? <div className="text-xs text-ink-mid">Not available on this API yet.</div> : <ContactsSummary contacts={contactsQuery.data} workerId={workerId} />}
        <p className="mt-1 text-[10px] text-ink-mid/80">The worker adds or removes a channel from their own app; nobody adds one for them.</p>
      </div>
      <div>
        <div className="mb-1.5 flex flex-wrap items-baseline gap-x-2 text-[9px] font-bold uppercase tracking-wide text-ink-mid">
          Where their messages went
          <EndpointHint>GET /workers/{'{workerId}'}/deliveries</EndpointHint>
        </div>
        {deliveriesQuery.isPending ? <Skeleton /> : deliveriesQuery.isError ? <div className="text-xs text-ink-mid">Not available on this API yet.</div> : <DeliveryReceiptsList receipts={deliveriesQuery.data.items} empty="Nothing has gone beyond the app yet: a Bridge alert or a reply to a note is pushed, emailed or texted once the worker turns a channel on." />}
      </div>
    </div>
  )
}
